import { useMemo } from 'react';
import styles from './IdentityGlyph.module.css';

type IdentityGlyphProps = Readonly<{
  glyphHex: string | null;
}>;

const FACETS = 12;
const MIN_R = 20;
const MAX_R = 75;
const SIZE = 180;
const HUE_SPREAD = 60;
const SAT = 60;
const LIGHT_MIN = 50;
const LIGHT_MAX = 70;
const OPACITY_MIN = 0.30;
const OPACITY_MAX = 0.70;
const WIDTH_MIN = 0.15;
const WIDTH_MAX = 0.55;
const STROKE_W = 0.5;
const STROKE_OP = 0.60;
const CENTER_R = 3;

const CX = 100;
const CY = 100;
const START_ANGLE = -Math.PI / 2;
const ANGLE_STEP = (2 * Math.PI) / FACETS;

type PetalData = Readonly<{
  path: string;
  fill: string;
  stroke: string;
}>;

type GlyphComputed = Readonly<{
  petals: readonly PetalData[];
  baseHue: number;
}>;

const hexToFloats = (hex: string): Float32Array => {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return new Float32Array(bytes.buffer);
};

const djb2Hash = (floats: Float32Array): number => {
  let hash = 5381;
  for (let i = 0; i < floats.length; i += 8) {
    const bits = Math.round(floats[i] * 1000000);
    hash = ((hash << 5) + hash + bits) & 0x7fffffff;
  }
  return hash;
};

const cosineSimilarity = (a: Float32Array, b: Float32Array): number => {
  let dot = 0;
  let magA = 0;
  let magB = 0;
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    dot += a[i] * b[i];
    magA += a[i] * a[i];
    magB += b[i] * b[i];
  }
  const denom = Math.sqrt(magA) * Math.sqrt(magB);
  if (denom < 1e-10) return 0;
  return dot / denom;
};

const computeGlyph = (hex: string): GlyphComputed => {
  const floats = hexToFloats(hex);
  const groupSize = Math.floor(floats.length / FACETS);
  const baseHue = djb2Hash(floats) % 360;

  // Extract chunks
  const chunks: Float32Array[] = Array.from({ length: FACETS }, (_, i) =>
    floats.slice(i * groupSize, (i + 1) * groupSize)
  );

  // NxN cosine similarity matrix
  const sim: number[][] = Array.from({ length: FACETS }, (_, i) =>
    Array.from({ length: FACETS }, (__, j) =>
      i === j ? 1 : cosineSimilarity(chunks[i], chunks[j])
    )
  );

  // Per-facet metrics
  const rawDistinctiveness = Array.from({ length: FACETS }, (_, i) => {
    let sumAbsSim = 0;
    for (let j = 0; j < FACETS; j++) {
      if (j !== i) sumAbsSim += Math.abs(sim[i][j]);
    }
    return 1 - sumAbsSim / (FACETS - 1);
  });

  const rawAlignment = Array.from({ length: FACETS }, (_, i) => {
    const prev = (i - 1 + FACETS) % FACETS;
    const next = (i + 1) % FACETS;
    return (sim[i][prev] + sim[i][next]) / 2;
  });

  const polarity = Array.from({ length: FACETS }, (_, i) => {
    let posSim = 0;
    let negSim = 0;
    for (let j = 0; j < FACETS; j++) {
      if (j === i) continue;
      if (sim[i][j] > 0) posSim += sim[i][j];
      else negSim += Math.abs(sim[i][j]);
    }
    const total = posSim + negSim;
    return total < 1e-10 ? 0.5 : posSim / total;
  });

  // Normalize to [0,1]
  const minD = Math.min(...rawDistinctiveness);
  const maxD = Math.max(...rawDistinctiveness);
  const rangeD = maxD - minD || 1;
  const normDistinctiveness = rawDistinctiveness.map((d) => (d - minD) / rangeD);

  const minA = Math.min(...rawAlignment);
  const maxA = Math.max(...rawAlignment);
  const rangeA = maxA - minA || 1;
  const normAlignment = rawAlignment.map((a) => (a - minA) / rangeA);

  // Build petals
  const petals: PetalData[] = Array.from({ length: FACETS }, (_, i) => {
    const angle = START_ANGLE + i * ANGLE_STEP;
    const normDist = normDistinctiveness[i];
    const normAlign = normAlignment[i];
    const pol = polarity[i];

    const r = MIN_R + normDist * (MAX_R - MIN_R);
    const halfWidth = ANGLE_STEP * (WIDTH_MIN + normAlign * (WIDTH_MAX - WIDTH_MIN));

    // Tip
    const tipX = CX + r * Math.cos(angle);
    const tipY = CY + r * Math.sin(angle);

    // Left/right bases at MIN_R
    const leftAngle = angle - halfWidth;
    const rightAngle = angle + halfWidth;
    const leftX = CX + MIN_R * Math.cos(leftAngle);
    const leftY = CY + MIN_R * Math.sin(leftAngle);
    const rightX = CX + MIN_R * Math.cos(rightAngle);
    const rightY = CY + MIN_R * Math.sin(rightAngle);

    // Center point
    const centerDist = MIN_R * 0.5;
    const centerX = CX + centerDist * Math.cos(angle);
    const centerY = CY + centerDist * Math.sin(angle);

    // Quadratic bezier petal path
    const path = [
      `M${leftX},${leftY}`,
      `Q${tipX},${tipY} ${rightX},${rightY}`,
      `L${centerX},${centerY}`,
      'Z',
    ].join(' ');

    // Color — hue spreads ±HUE_SPREAD around base, polarity nudges ±(HUE_SPREAD*0.25)
    const indexSpread = (i / FACETS) * HUE_SPREAD * 2 - HUE_SPREAD;
    const polarityNudge = (pol - 0.5) * HUE_SPREAD * 0.5;
    const hue = (baseHue + indexSpread + polarityNudge + 360) % 360;
    const light = LIGHT_MIN + normDist * (LIGHT_MAX - LIGHT_MIN);
    const opacity = OPACITY_MIN + normDist * (OPACITY_MAX - OPACITY_MIN);

    const fill = `hsla(${hue}, ${SAT}%, ${light}%, ${opacity})`;
    const stroke = `hsla(${hue}, ${SAT + 10}%, 65%, ${STROKE_OP})`;

    return { path, fill, stroke };
  });

  return { petals, baseHue };
};

const IdentityGlyph = ({ glyphHex }: IdentityGlyphProps): React.ReactElement => {
  const glyphData = useMemo((): GlyphComputed | null => {
    if (!glyphHex || glyphHex.length === 0) return null;
    return computeGlyph(glyphHex);
  }, [glyphHex]);

  if (glyphData === null) {
    return (
      <div className={styles.container}>
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Identity Glyph">
          <text
            x={SIZE / 2}
            y={SIZE / 2}
            fill="rgba(157,143,255,0.3)"
            fontSize={12}
            textAnchor="middle"
            dominantBaseline="central"
            data-testid="empty-glyph"
          >
            No glyph computed yet
          </text>
        </svg>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Identity Glyph">
        {/* Petal paths */}
        {glyphData.petals.map((petal, i) => (
          <path
            key={`petal-${i}`}
            d={petal.path}
            fill={petal.fill}
            stroke={petal.stroke}
            strokeWidth={STROKE_W}
            data-testid={`petal-${i}`}
          />
        ))}

        {/* Center dot */}
        <circle
          cx={CX}
          cy={CY}
          r={CENTER_R}
          fill={`hsl(${glyphData.baseHue}, ${SAT}%, 70%)`}
          opacity={0.8}
          data-testid="center-dot"
        />
      </svg>
    </div>
  );
};

export { IdentityGlyph };
export type { IdentityGlyphProps };
